import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import BaseURL from '../../assets/contants/BaseURL';
import widgetRegistry from './widgetRegistry';
import { compactWidgets } from './gridUtils';
import { getVariantMeta } from './variantFamilies';

const STORAGE_KEY = 'ems_custom_dashboard_layout_v3';
const DEFAULT_GRID_COLS = 12;

function fromRegistry(type, x, rowGroup, id, variant, overrides = {}) {
    const meta = widgetRegistry[type];
    const chosenVariant = variant || meta.defaultVariant;
    const variantMeta = getVariantMeta(meta.variantFamily, chosenVariant);
    const layout = variantMeta?.layout || meta.defaultLayout;
    return {
        id,
        type,
        title: overrides.title || variantMeta?.title || meta.defaultTitle,
        variant: chosenVariant,
        layout: {
            x, y: rowGroup, w: overrides.layout?.w ?? layout.w, h: overrides.layout?.h ?? layout.h,
            minW: layout.minW, minH: layout.minH,
        },
        settings: { ...meta.defaultSettings, ...overrides.settings },
        dataSource: { ...meta.defaultDataSource },
    };
}

const DEFAULT_WIDGETS_RAW = [
    fromRegistry('voltage-gauge', 0, 0, 'widget-voltage-gauge-default', undefined, { title: 'Voltage R' }),
    fromRegistry('current-gauge', 2, 0, 'widget-current-gauge-default', undefined, { title: 'Current R' }),
    fromRegistry('power-gauge', 4, 0, 'widget-power-gauge-default', undefined, { title: 'Power (kW)' }),
    fromRegistry('kva-gauge', 6, 0, 'widget-kva-gauge-default', undefined, { title: 'Apparent Power (kVA)' }),
    fromRegistry('frequency-gauge', 8, 0, 'widget-frequency-gauge-default'),
    fromRegistry('pf-gauge', 10, 0, 'widget-pf-gauge-default', undefined, { title: 'Power Factor' }),
    fromRegistry('metric', 0, 1, 'widget-metric-default', 'custom', {
        title: 'Voltage / Current / PF Detail',
        layout: { w: 4, h: 6 },
        settings: {
            customShowTrend: true,
            customFields: [
                { title: 'Voltage YB', unit: 'V', position: 'top-left' },
                { title: 'Voltage BR', unit: 'V', position: 'top-center' },
                { title: 'Average Voltage', unit: 'V', position: 'top-right' },
                { title: 'Current Y', unit: 'A', position: 'middle-left' },
                { title: 'Current B', unit: 'A', position: 'center' },
                { title: 'Power Factor R', unit: '', position: 'middle-right' },
                { title: 'Power Factor Y', unit: '', position: 'bottom-left' },
                { title: 'Power Factor B', unit: '', position: 'bottom-center' },
                { title: 'Power Factor Total', unit: '', position: 'bottom-right' },
            ],
        },
    }),
    fromRegistry('progress', 4, 1, 'widget-progress-default', undefined, { title: 'Total Current' }),
    fromRegistry('status-list', 6, 1, 'widget-status-list-default'),
    fromRegistry('table', 10, 1, 'widget-table-default'),
    fromRegistry('trend-chart', 0, 2, 'widget-trend-chart-default', undefined, { title: 'Total Energy (kWh)' }),
    fromRegistry('thd-chart', 6, 2, 'widget-thd-chart-default'),
    fromRegistry('more-widgets', 0, 3, 'widget-sparkline-default', 'sparkline', { title: 'Energy (kWh)' }),
    fromRegistry('more-widgets', 3, 3, 'widget-comparison-default', 'comparison', { title: 'Voltage B' }),
    fromRegistry('more-widgets', 6, 3, 'widget-clock-default', 'clock'),
    fromRegistry('more-widgets', 9, 3, 'widget-ring3d-default', 'ring3d', { title: 'Voltage RY' }),
    fromRegistry('more-widgets', 0, 4, 'widget-alert-feed-default', 'alert-feed'),
    fromRegistry('more-widgets', 6, 4, 'widget-note-default', 'note'),
    fromRegistry('metric', 0, 5, 'widget-metric-detail2-default', 'custom', {
        title: 'Harmonics Detail',
        layout: { w: 4, h: 3 },
        settings: {
            customShowTrend: true,
            customFields: [
                { title: 'Voltage THD Avg', unit: '%', position: 'middle-left' },
                { title: 'Current THD Avg', unit: '%', position: 'middle-right' },
            ],
        },
    }),
];

export const DEFAULT_WIDGETS = compactWidgets(DEFAULT_WIDGETS_RAW, DEFAULT_GRID_COLS);

function loadFromLocalStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        console.error('useDashboardLayout: failed to parse saved layout', err);
        return null;
    }
}

function saveToLocalStorage(widgets) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
}

async function fetchLayoutFromAPI() {
    const token = sessionStorage.getItem('token');
    const res = await axios.get(`${BaseURL}meters/dashboard-widgets/`, {
        headers: { Authorization: `Token ${token}` },
    });
    return res.data?.widgets ?? null;
}

async function saveLayoutToAPI(widgets) {
    const token = sessionStorage.getItem('token');
    await axios.post(`${BaseURL}meters/dashboard-widgets/`, { widgets }, {
        headers: { Authorization: `Token ${token}` },
    });
}

export default function useDashboardLayout({ persistMode = 'localStorage' } = {}) {
    const [widgets, setWidgets] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (persistMode === 'api') {
                try {
                    const apiWidgets = await fetchLayoutFromAPI();
                    if (!cancelled) setWidgets(apiWidgets?.length ? apiWidgets : (loadFromLocalStorage() || DEFAULT_WIDGETS));
                } catch (err) {
                    console.error('useDashboardLayout: API load failed, falling back to localStorage', err);
                    if (!cancelled) setWidgets(loadFromLocalStorage() || DEFAULT_WIDGETS);
                }
            } else {
                const stored = loadFromLocalStorage();
                if (!cancelled) setWidgets(stored?.length ? stored : DEFAULT_WIDGETS);
            }
            if (!cancelled) setIsLoaded(true);
        }

        load();
        return () => { cancelled = true; };
    }, [persistMode]);

    useEffect(() => {
        if (!isLoaded) return;
        saveToLocalStorage(widgets);
        if (persistMode === 'api') {
            saveLayoutToAPI(widgets)
                .then(() => setSaveError(null))
                .catch((err) => {
                    console.error('useDashboardLayout: API save failed', err);
                    setSaveError(err);
                });
        }
    }, [widgets, isLoaded, persistMode]);

    const resetToDefault = useCallback(() => setWidgets(DEFAULT_WIDGETS), []);

    return { widgets, setWidgets, isLoaded, saveError, resetToDefault };
}
